import { Preview } from '@convergence/components';
import { Cell as CellType } from '@convergence/types';
import CodeMirror from '@uiw/react-codemirror';
import { Identifier } from 'dnd-core';
import { forwardRef, RefObject } from 'react';
import { client } from '../lib/client';
import { NotebookStore } from '../stores/NotebookStore';
import { CellOptions } from './Cell';
import { SkipForwardIcon } from '@phosphor-icons/react';
import { basicLight } from '@uiw/codemirror-theme-basic';
import { langs } from '@uiw/codemirror-extensions-langs';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { observer } from 'mobx-react';

interface CodeCellProps {
  cell: CellType;
  index: number;
  readonly: boolean;
  store: NotebookStore;
  activeCellId: string | null;
  ref: RefObject<HTMLDivElement>;
  isDragging: boolean;
  handlerId: Identifier | null;
  tinkerBoxId?: number;
}

export const CodeCell = observer(
  forwardRef<HTMLDivElement, CodeCellProps>(
    (
      {
        cell,
        index,
        readonly,
        store,
        activeCellId,
        isDragging,
        handlerId,
        tinkerBoxId
      },
      ref
    ) => {
      const languageMap = {
        markdown: langs.markdown,
        bash: langs.bash,
        python: langs.py
      } as const;
      const langSchema = z.union([
        z.literal('markdown'),
        z.literal('bash'),
        z.literal('python')
      ]);
      const lang = langSchema.parse(
        'bash'
        // store.notebookContent?.metadata.language_info.name
      );

      const cellSourceStr =
        typeof cell.source === 'string' ? cell.source : cell.source.join('\n');

      const { mutateAsync: runCode, isPending } = useMutation({
        mutationKey: ['runCodeCell'],
        mutationFn: async () => {
          cell.results = [];
          await store.onRunCode(cellSourceStr, cell.id);
        }
      });

      return (
        <div
          style={{ opacity: isDragging ? 0.5 : 1 }}
          className="relative flex gap-1"
          onClick={() => store.activateCell(cell.id)}
        >
          <div
            className={`w-3 cursor-move ${
              activeCellId === cell.id ? 'bg-orange-2' : 'bg-none'
            }`}
            ref={ref}
            data-handler-id={handlerId}
          />
          <div className="grow">
            <div className="py-2 flex gap-1">
              <button onClick={async () => await runCode()}>
                <SkipForwardIcon size={18} />
              </button>
              <CodeMirror
                key={cell.id}
                className="w-full overflow-x-auto"
                value={cellSourceStr}
                theme={basicLight}
                extensions={[languageMap[lang]()]}
                editable={!readonly}
                onChange={(code) => {
                  store.updateCell(code, cell.id);
                }}
              />
            </div>
            {isPending && (
              <div className="px-4 py-2 bg-gray-100 rounded-sm">
                <div className="pending-code" />
              </div>
            )}
            {cell.results && cell.results.length > 0 && (
              <div className="pb-2">
                <pre className="px-4 py-2 bg-gray-100 rounded-sm whitespace-pre-wrap w-full overflow-x-auto">
                  {cell.results.map((res) => {
                    if (res.show) {
                      const url = `${client.endpoint}/tinker-box-connect/${tinkerBoxId}?filepath=${res.content}`;
                      return <Preview filePath={url} />;
                    } else {
                      return (
                        <p
                          className={
                            res.is_error ? `text-red-600` : `text-green-600`
                          }
                        >
                          {res.content}
                        </p>
                      );
                    }
                  })}
                </pre>
              </div>
            )}
          </div>
          {activeCellId === cell.id && !readonly && (
            <CellOptions
              cellIndex={index}
              cellId={cell.id}
              cellType={cell.cell_type}
              deleteCell={store.deleteCell}
              moveCell={store.moveCell}
              changeCellType={store.changeCellType}
            />
          )}
        </div>
      );
    }
  )
);
